import React from "react";
import PropTypes from "prop-types";
import { Grid } from "@material-ui/core";
import StepHeader from "../../components/StepHeader";
import Table from "../../../../../Table";
import DeleteEmployeesTableBenefitCell from "../../components/DeleteEmployeesTableBenefitCell";
import columns from "./utils";
import reviewEmployeesToDelete from "../../../../../../../usecases/employees/review-employees-to-delete";
import EmptyDataForTableMessage from "../../components/EmptyDataForTableMessage";
import { translate as t } from "../../../../../../../infrastructure/translate/localization";
import TableDataSpinner from "../../components/TableDataSpinner";
import StepFooter from "../../components/StepFooter";
import { MonetaryAmount } from "../../../../../../domain/monetary-amount";

export default function CurrentMonthConsumptions({
  employees,
  currentStep,
  content,
  data,
  adjustmentsPagination,
  isLoading,
  country,
  target,
}) {
  const parsedData = data.adjustments.map(({ employee, fundsGiven, flex }) => {
    const amountExtractor = (property) => {
      return {
        flexValue: new MonetaryAmount(
          flex?.[property] ? flex?.[property].amountInCents : 0,
          flex?.[property] ? flex?.[property].currency : null
        ).prettifyMoney({}),
        benefitValue: new MonetaryAmount(
          fundsGiven?.[property] ? fundsGiven?.[property].amountInCents : 0,
          fundsGiven?.[property] ? fundsGiven?.[property].currency : null
        ).prettifyMoney({}),
      };
    };

    return {
      legalId: employee.legalId,
      nameAndSurname: `${employee.name} ${employee.surname}`,
      meals: (
        <DeleteEmployeesTableBenefitCell {...amountExtractor("meal-benefit")} />
      ),
      transport: (
        <DeleteEmployeesTableBenefitCell
          {...amountExtractor("transport-benefit")}
        />
      ),
      healthInsurance: (
        <DeleteEmployeesTableBenefitCell
          {...amountExtractor("health-insurance-benefit")}
        />
      ),
      nursery: (
        <DeleteEmployeesTableBenefitCell
          {...amountExtractor("nursery-benefit")}
        />
      ),
      education: (
        <DeleteEmployeesTableBenefitCell
          {...amountExtractor("education-benefit")}
        />
      ),
    };
  });

  return (
    <Grid item container xs={12}>
      <StepHeader {...content} />
      {isLoading ? (
        <TableDataSpinner />
      ) : (
        <>
          {data.adjustments.length > 0 ? (
            <Table
              columns={columns(country)}
              data={parsedData}
              pageNumber={
                adjustmentsPagination?.pageNumber &&
                adjustmentsPagination?.pageNumber - 1
              }
              pageSize={adjustmentsPagination?.pageSize}
              totalRows={employees.length}
              onChange={(page) =>
                reviewEmployeesToDelete({
                  employees,
                  currentStep: 0,
                  adjustmentsPagination: {
                    pageNumber: page,
                    pageSize: adjustmentsPagination?.pageSize || 10,
                  },
                  target,
                })
              }
              onChangeRowsPerPage={(value) => {
                reviewEmployeesToDelete({
                  employees,
                  currentStep: 0,
                  adjustmentsPagination: {
                    ...adjustmentsPagination,
                    pageSize: value,
                    target,
                  },
                });
              }}
            />
          ) : (
            <EmptyDataForTableMessage
              message={t("common-messages.empty-data-for-selected-employees", {
                dataName: t(
                  "employees-section.current-month-consumptions"
                ).toLowerCase(),
              })}
            />
          )}
        </>
      )}
      <StepFooter
        isLoading={isLoading}
        currentStep={currentStep}
        employees={employees}
        hasNoData={data?.adjustments.length === 0}
        target={target}
      />
    </Grid>
  );
}

CurrentMonthConsumptions.defaultProps = {
  employees: [],
  data: { adjustments: [] },
  adjustmentsPagination: undefined,
  isLoading: false,
  currentStep: 0,
  country: "ES",
};

CurrentMonthConsumptions.propTypes = {
  employees: PropTypes.array,
  data: PropTypes.object,
  content: PropTypes.object.isRequired,
  adjustmentsPagination: PropTypes.object,
  isLoading: PropTypes.bool,
  currentStep: PropTypes.number,
  country: PropTypes.string,
};
